import { createContext, type ReactNode } from "react";
import type { Href } from "@-/com.npmjs.expo-router";
import { defaultError } from "@-/com.npmjs.react-context-default-error";

/** Published by `<PrimaryControlsStripBack hidden />` to suppress the host's default chevron fallback. */
export const PRIMARY_CONTROLS_STRIP_BACK_HIDDEN = Symbol("PrimaryControlsStripBackHidden");

export type PrimaryControlsStripBackAppShellPlacement = "account-drawer" | "toolbar";

/**
 * A labeled back control published by a screen. `fallbackHref` is REQUIRED: it is where the
 * control navigates when there is no in-tab history to pop (deep link, restored state), so a
 * rendered back button can never dispatch an unhandled — or cross-tab — GO_BACK.
 */
export type PrimaryControlsStripBackRegistryLabelEntry = {
  appShellPlacement?: PrimaryControlsStripBackAppShellPlacement;
  fallbackHref: Href;
  label: ReactNode;
};

/**
 * Published by `<PrimaryControlsStripLeading>`: the screen fills the app shell's top-left slot itself (a main
 * page's More menu, Settings' profile button) instead of the default back control. Each publisher
 * owns a distinct object, so it can tell whether its own content is the one on screen.
 */
export type PrimaryControlsStripBackRegistryOverrideEntry = { kind: "override" };

export type PrimaryControlsStripBackRegistryEntry =
  | PrimaryControlsStripBackRegistryLabelEntry
  | PrimaryControlsStripBackRegistryOverrideEntry
  | typeof PRIMARY_CONTROLS_STRIP_BACK_HIDDEN;

export function isPrimaryControlsStripBackOverrideEntry(
  entry: PrimaryControlsStripBackRegistryEntry | null,
): entry is PrimaryControlsStripBackRegistryOverrideEntry {
  return entry !== null && entry !== PRIMARY_CONTROLS_STRIP_BACK_HIDDEN && "kind" in entry;
}

type HostListener = (entry: PrimaryControlsStripBackRegistryEntry | null) => void;

/**
 * How deep in the navigation tree the publishing screen sits. The registry resolves ties with it
 * so the DEEPEST publisher wins, rather than whichever component happened to re-render last.
 */
export type PrimaryControlsStripBackPublishOptions = { depth: number };

export type PrimaryControlsStripBackRegistry = {
  getCurrent: () => PrimaryControlsStripBackRegistryEntry | null;
  publish: (
    entry: PrimaryControlsStripBackRegistryEntry,
    options: PrimaryControlsStripBackPublishOptions,
  ) => { unpublish: () => void; update: (entry: PrimaryControlsStripBackRegistryEntry) => void };
  subscribe: (listener: HostListener) => () => void;
};

type Slot = {
  depth: number;
  entry: PrimaryControlsStripBackRegistryEntry;
  /** Monotonic publish order, used only to break exact-depth ties deterministically. */
  sequence: number;
};

/**
 * Registry backing the app's single back control.
 *
 * WINNER SELECTION IS DEPTH-ORDERED, NOT RECENCY-ORDERED. The previous implementation returned
 * the last entry in `Map` iteration order, which is *insertion* order — so any publisher that
 * re-published (which happened on every render, because a `ReactNode` label is a new object each
 * time) jumped to the front of the queue. With two mounted layouts publishing at once — the common
 * case, since a tab-stack layout and the screen pushed on top of it are both mounted and both
 * report `useIsFocused() === true` within their own navigators — the winner flip-flopped with
 * render order. That is the root cause of the back control appearing in the wrong place, showing
 * the wrong label, or vanishing entirely.
 *
 * Depth ordering makes the outcome structural: the deepest mounted publisher is by definition the
 * screen closest to what the user is actually looking at, so it wins regardless of render timing.
 * At an exact depth tie an override beats a back entry — a layout that both hides the back control
 * and renders its menu in the slot publishes both at its own depth — and `sequence` breaks any
 * remaining tie. Updates are done in place so a re-render never changes the winner.
 */
export function createPrimaryControlsStripBackRegistry(): PrimaryControlsStripBackRegistry {
  const slots = new Map<number, Slot>();
  let nextId = 0;
  let nextSequence = 0;
  const listeners = new Set<HostListener>();

  const getCurrent = (): PrimaryControlsStripBackRegistryEntry | null => {
    let winner: Slot | null = null;
    for (const slot of slots.values()) {
      if (winner === null || slot.depth > winner.depth) {
        winner = slot;
        continue;
      }
      if (slot.depth < winner.depth) {
        continue;
      }
      const isOverride = isPrimaryControlsStripBackOverrideEntry(slot.entry);
      const isWinnerOverride = isPrimaryControlsStripBackOverrideEntry(winner.entry);
      if (
        (isOverride && !isWinnerOverride) ||
        (isOverride === isWinnerOverride && slot.sequence > winner.sequence)
      ) {
        winner = slot;
      }
    }
    return winner === null ? null : winner.entry;
  };

  let lastNotified: PrimaryControlsStripBackRegistryEntry | null = null;
  const notify = () => {
    const current = getCurrent();
    // Publishing re-runs whenever a label node identity changes, which for a JSX label is every
    // render. Only wake subscribers when the winning entry actually changed, so the host does not
    // re-render (and re-measure its layout lane) on every parent render.
    if (current === lastNotified) {
      return;
    }
    lastNotified = current;
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent,
    publish(entry, options) {
      const id = ++nextId;
      slots.set(id, { depth: options.depth, entry, sequence: ++nextSequence });
      notify();
      return {
        unpublish: () => {
          if (slots.delete(id)) {
            notify();
          }
        },
        update: (nextEntry) => {
          const slot = slots.get(id);
          if (slot === undefined) {
            return;
          }
          // Update IN PLACE: keep the original depth and sequence so a re-render can never
          // promote this publisher over a deeper one.
          slots.set(id, { ...slot, entry: nextEntry });
          notify();
        },
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const PrimaryControlsStripBackContext = createContext(
  defaultError<PrimaryControlsStripBackRegistry>("PrimaryControlsStripBackContext"),
);
